import { Box, Typography, Container, Paper, Button } from "@mui/material";
import Image from "next/image";
import ScrollReveal from "./ScrollReveal";
import ExpectedTimeline from "./ExpectedTimeline";
import { IMAGES } from "@/constants/images";
import { useTranslation } from "react-i18next"; // ← ADD

export default function OnboardingTimeline({ onClaimClick }) {
  const { t, i18n } = useTranslation(); // ← ADD
  const isArabic = i18n.language === "ar"; // ← ADD

  const stepsRaw = t("onboardingTimeline.steps", { returnObjects: true });
  const stepsData = Array.isArray(stepsRaw) ? stepsRaw : [];

  const stepIcons = [
    IMAGES.HN_ONE_IMG,
    IMAGES.HN_TWO_IMG,
    IMAGES.HN_THREE_IMG,
    IMAGES.HN_FOUR_IMG,
  ];

  const steps = stepsData.map((step, i) => ({
    number: i + 1,
    icon: stepIcons[i],
    title: step.title,
    description: step.description,
  }));

  return (
    <Box
      sx={{
        backgroundColor: "#fafafa",
        py: { xs: 5, sm: 7, md: 8 },
        px: { xs: 2, sm: 3 },
        direction: isArabic ? "rtl" : "ltr", // ← ADD
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <ScrollReveal delay={0.1} duration={1.0}>
          <Box
            sx={{
              textAlign: isArabic
                ? { xs: "right", sm: "right", md: "right", lg: "center" }
                : { xs: "left", sm: "left", md: "left", lg: "center" }, // ← FIX
              mb: { xs: 5, sm: 6 },
            }}
          >
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: "bold",
                color: "#111827",
                fontSize: { xs: "1.6rem", sm: "1.7rem" },
                fontFamily: "var(--font-plusjakarta)",
              }}
            >
              {t("onboardingTimeline.title")} {/* ← REPLACE */}
            </Typography>
            <Typography
              sx={{
                color: "#4B5563",
                fontSize: { xs: "0.9rem", sm: "1rem" },
                fontFamily: "var(--font-outfit)",
                mt: ".7%",
              }}
            >
              {t("onboardingTimeline.subtitle")} {/* ← REPLACE */}
            </Typography>
          </Box>
        </ScrollReveal>

        {/* Steps */}
        <Box sx={{ mb: { xs: 6, sm: 8 }, maxWidth: 780, mx: "auto" }}>
          {steps.map((step, index) => (
            <ScrollReveal
              key={step.number}
              delay={0.2 + index * 0.1}
              duration={1.0}
            >
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: {
                      xs: "column",
                      sm: "column",
                      md: "column",
                      lg: "row",
                    },
                    gap: { xs: 2.5, sm: 3 },
                    alignItems: "flex-start",
                  }}
                >
                  {/* Left Side - Number */}
                  <Box
                    sx={{
                      display: {
                        xs: "none",
                        sm: "none",
                        md: "flex",
                        lg: "flex",
                      },
                      flexDirection: "column",
                      alignItems: "center",
                      position: "relative",
                      pt: 0.5,
                      mt: "3%",
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "50%",
                        background: "linear-gradient(90deg, #A467FF, #7F29FF)",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                        fontSize: "1.2rem",
                        flexShrink: 0,
                        zIndex: 2,
                        fontFamily: "var(--font-outfit)",
                      }}
                    >
                      {step.number}
                    </Box>
                    {index < steps.length - 1 && (
                      <Box
                        sx={{
                          width: 2,
                          height: "calc(100% + 70px)",
                          position: "absolute",
                          top: 52,
                          left: "50%",
                          transform: "translateX(-50%)",
                          backgroundImage:
                            "repeating-linear-gradient(0deg, #C9A4FF, #C9A4FF 8px, transparent 8px, transparent 16px)",
                        }}
                      />
                    )}
                  </Box>

                  {/* Right Side - Card */}
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      p: { xs: 2.5, sm: 3 },
                      mb: index < steps.length - 1 ? 3 : 0,
                      borderRadius: 2,
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      boxShadow:
                        "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
                      display: "flex",
                      flexDirection: {
                        xs: "column",
                        sm: "column",
                        md: "column",
                        lg: "row",
                      },
                      gap: { xs: 2, sm: 2.5 },
                      alignItems: "flex-start",
                      textAlign: isArabic ? "right" : "left", // ← FIX
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 40, sm: 44 },
                        height: { xs: 40, sm: 44 },
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Image
                        src={step.icon}
                        alt={`Step ${step.number}`}
                        width={50}
                        height={50}
                        style={{ objectFit: "contain" }}
                      />
                    </Box>

                    <Box sx={{ flex: 1, pt: 0.25 }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: "#0f172a",
                          mb: 0.5,
                          fontSize: { xs: "1rem", sm: "1.1rem" },
                          lineHeight: 1.4,
                          fontFamily: "var(--font-outfit)",
                        }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#6F7B8D",
                          fontSize: { xs: "0.875rem", sm: "0.9rem" },
                          fontFamily: "var(--font-outfit)",
                          fontWeight: 400,
                        }}
                      >
                        {step.description}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            </ScrollReveal>
          ))}

          {/* Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: isArabic
                ? { xs: "flex-start", sm: "flex-start", md: "center" }
                : { xs: "flex-start", sm: "flex-start", md: "center" }, // ← FIX
              mt: "5%",
            }}
          >
            <Button
              variant="contained"
              onClick={onClaimClick}
              sx={{
                backgroundColor: "#9f5fff",
                color: "white",
                textTransform: "none",
                fontSize: { xs: "0.64rem", sm: "0.95rem", md: "1rem" },
                fontWeight: 500,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                boxShadow: "none",
                transition: "transform 0.2s ease-in-out",
                fontFamily: "var(--font-outfit)",
                "&:hover": {
                  backgroundColor: "#9f5fff",
                  boxShadow: "none",
                  transform: "scale(1.05) translateY(-2px)",
                },
              }}
            >
              {t("onboardingTimeline.cta")} {/* ← REPLACE */}
            </Button>
          </Box>
        </Box>

        {/* Expected Timeline */}
        <ScrollReveal delay={0.3} duration={1.2}>
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "15px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
            }}
          >
            <ExpectedTimeline />
          </Box>
        </ScrollReveal>
      </Container>
    </Box>
  );
}
